import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { JsonataEditorComponent } from '../../../shared/components/jsonata-editor/jsonata-editor.component';
import { JsonataExpressionTemplate } from '../../../shared/components/jsonata-editor/jsonata-expression-template';

@Component({
    selector: 'app-formly-jsonata-editor',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, FormlyModule, TranslatePipe, JsonataEditorComponent],
    template: `
        @if (hintKey) {
            <p class="text-sm text-muted-color mb-2">{{ hintKey | translate }}</p>
        }
        <div class="flex w-full flex-col" [style.min-height.px]="minHeightPx">
            <phx-jsonata-editor
                class="w-full flex-1"
                [style.min-height.px]="minHeightPx"
                [expression]="expression"
                (expressionChange)="onExpressionChange($event)"
                [source]="source"
                [sourceReadOnly]="sourceReadOnly"
                [expressionTemplates]="expressionTemplates"
                [dataTestIdPrefix]="dataTestIdPrefix"
            />
        </div>
    `,
})
export class FormlyJsonataEditorComponent extends FieldType implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdRef = inject(ChangeDetectorRef);

    expression = '';
    source: unknown = {};
    sourceReadOnly = false;
    expressionTemplates: JsonataExpressionTemplate[] = [];
    hintKey = 'common.jsonata-editor.hint';
    dataTestIdPrefix = 'formly-jsonata-editor';
    minHeightPx = 600;

    ngOnInit(): void {
        this.source = this.props?.jsonataEditorSource ?? {};
        this.sourceReadOnly = !!this.props?.sourceReadOnly;
        this.expressionTemplates = this.props?.expressionTemplates ?? [];
        this.hintKey = this.props?.hintKey ?? 'common.jsonata-editor.hint';
        this.dataTestIdPrefix = String(this.props?.attributes?.['data-testid'] ?? 'formly-jsonata-editor');
        this.minHeightPx = Number(this.props?.minHeightPx) > 0 ? Number(this.props.minHeightPx) : 800;

        this.expression = this.formControl.value ?? '';
        this.formControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                const next = value ?? '';
                if (next !== this.expression) {
                    this.expression = next;
                    this.cdRef.markForCheck();
                }
            });
    }

    onExpressionChange(value: string): void {
        this.expression = value;
        if (this.formControl.value !== value) {
            this.formControl.setValue(value);
            this.formControl.markAsDirty();
        }
    }
}
